import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { getAllWorkOrders, type WorkOrderResponse } from '@/api/workorder.api';
import { useToast } from '@/contexts/ToastContext';
import { getAllWorkCenters, type WorkCenterResponse } from '@/api/workcenter.api';
import { getAllMachines, type MachineResponse } from '@/api/machine.api';
import { getAllProductionOrders, type ProductionOrderResponse } from '@/api/productionOrder.api';
import SearchableSelect from '@/components/base/SearchableSelect';
import { formatDateIST, toInputDate } from '@/utils/format';
import { formatWorkCenter } from '@/hooks/useWorkCenters';

/* ─── Standardized Types ─── */
type ViewMode = 'week' | '2weeks' | 'month';

interface Filters {
  workCenter: string;
  status: string;
  productionOrder: string;
}

interface ScheduleWorkOrder {
  id: string;
  woNumber: string;
  productionOrderId: string;
  productionOrderNumber: string;
  stageNumber: number;
  stageName: string;
  workCenterId: string;
  workCenterName: string;
  machineId: string | null;
  machineName: string | null;
  operatorId: string | null;
  operatorName: string | null;
  shiftId: string | null;
  shiftName: string | null;
  plannedQty: number;
  completedQty: number;
  rejectedQty: number;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'QC_PENDING' | 'COMPLETED_QC_PENDING' | 'QC_FAILED';
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate: string | null;
  actualEndDate: string | null;
  plannedTimeMinutes: number;
  /** People needed on the stage, and whether a sub-contractor does it */
  operatorsRequired?: number;
  stageType?: string;
  subcontractorName?: string | null;
  actualTimeMinutes: number | null;
  notes: string | null;
  warehouseId: string | null;
}

interface ScheduleWorkCenter {
  id: string;
  /** Short code (WC-001), shown before the name */
  code?: string;
  name: string;
  type: string;
  capacityPerHour: number;
  warehouseId?: string | null;
  /** Working hours per day and the productive share of them (%) */
  hoursPerDay: number;
  efficiencyPct: number;
  /** Days its shifts run (Mon, Tue …); empty = every day */
  workingDays: string[];
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface ScheduleMachine {
  id: string;
  name: string;
  workCenterId: string | null;
}

interface ScheduleProductionOrder {
  id: string;
  poNumber: string;
  productName?: string;
  variantName?: string;
}

/* ─── Helpers ─── */
function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

// Local midnight of a date, so day arithmetic is not thrown off by time-of-day or timezone
function startOfDay(value: string | Date): Date {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

// "YYYY-MM-DD" from a date input, read as a local calendar date (not UTC)
function parseInputDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// Whole calendar days from a to b
function daysBetween(a: string | Date, b: string | Date): number {
  const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isWeekend(d: Date): boolean {
  const day = d.getDay();
  return day === 0 || day === 6;
}

function dayShort(d: Date): string {
  return d.toLocaleDateString('en-IN', { weekday: 'short' });
}

function dayNum(d: Date): number {
  return d.getDate();
}

function getViewDays(mode: ViewMode): number {
  if (mode === 'week') return 7;
  if (mode === '2weeks') return 14;
  return 30;
}

function getStatusBarClasses(status: ScheduleWorkOrder['status']): string {
  switch (status) {
    case 'PENDING':
      return 'bg-blue-200 text-blue-800 border border-blue-300';
    case 'IN_PROGRESS':
      return 'bg-amber-200 text-amber-800 border border-amber-300';
    case 'COMPLETED':
      return 'bg-emerald-200 text-emerald-800 border border-emerald-300';
    case 'ON_HOLD':
      return 'bg-rose-100 text-rose-700 border border-rose-200';
    case 'QC_PENDING':
    case 'COMPLETED_QC_PENDING':
      return 'bg-purple-200 text-purple-800 border border-purple-300';
    case 'QC_FAILED':
      return 'bg-red-200 text-red-800 border border-red-300';
    default:
      return 'bg-gray-200 text-gray-700 border border-gray-300';
  }
}

function getStatusBadgeClasses(status: ScheduleWorkOrder['status']): string {
  switch (status) {
    case 'PENDING':
      return 'bg-gray-100 text-gray-700';
    case 'IN_PROGRESS':
      return 'bg-amber-100 text-amber-700';
    case 'COMPLETED':
      return 'bg-emerald-100 text-emerald-700';
    case 'ON_HOLD':
      return 'bg-rose-100 text-rose-700';
    case 'QC_PENDING':
    case 'COMPLETED_QC_PENDING':
      return 'bg-purple-100 text-purple-700';
    case 'QC_FAILED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function getStatusDotClasses(status: ScheduleWorkOrder['status']): string {
  switch (status) {
    case 'PENDING':
      return 'bg-blue-500';
    case 'IN_PROGRESS':
      return 'bg-amber-500';
    case 'COMPLETED':
      return 'bg-emerald-500';
    case 'ON_HOLD':
      return 'bg-rose-500';
    case 'QC_PENDING':
    case 'COMPLETED_QC_PENDING':
      return 'bg-purple-500';
    case 'QC_FAILED':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
}

/* ─── Component ─── */
export default function ProductionSchedulePage() {
  const navigate = useNavigate();
  const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();

  /* API Data States */
  const [workOrders, setWorkOrders] = useState<ScheduleWorkOrder[]>([]);
  const [workCenters, setWorkCenters] = useState<ScheduleWorkCenter[]>([]);
  const [machines, setMachines] = useState<ScheduleMachine[]>([]);
  const [productionOrders, setProductionOrders] = useState<ScheduleProductionOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const toast = useToast();

  /* View state */
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [viewStartDate, setViewStartDate] = useState<Date>(() => {
    return startOfDay(new Date());
  });

  /* Filters */
  const [filters, setFilters] = useState<Filters>({
    workCenter: '',
    status: '',
    productionOrder: '',
  });

  /* Selected WO popup */
  const [selectedWO, setSelectedWO] = useState<ScheduleWorkOrder | null>(null);

  /* ─── Fetch All Data Dynamically ─── */
  const loadScheduleData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [woRes, wcRes, mcRes, poRes] = await Promise.allSettled([
        getAllWorkOrders(selectedWarehouseId || undefined),
        getAllWorkCenters(selectedWarehouseId || undefined),
        getAllMachines(selectedWarehouseId || undefined),
        getAllProductionOrders({ warehouseId: selectedWarehouseId || undefined, limit: 1000 }),
      ]);

      if (woRes.status === 'rejected') {
        toast.error(woRes.reason instanceof Error ? woRes.reason.message : 'Could not load work orders');
      }

      // 1. Process Work Orders
      let fetchedWOs: ScheduleWorkOrder[] = [];
      if (woRes.status === 'fulfilled' && woRes.value?.success && Array.isArray(woRes.value.data) && woRes.value.data.length > 0) {
        fetchedWOs = woRes.value.data.map((wo: WorkOrderResponse) => ({
          id: wo.id,
          woNumber: wo.wo_number || (wo as any).woNumber || '',
          productionOrderId: wo.production_order_id || (wo as any).productionOrderId || '',
          productionOrderNumber: wo.production_order_number || (wo as any).productionOrderNumber || '',
          stageNumber: Number(wo.stage_number || (wo as any).stageNumber || 1),
          stageName: wo.stage_name || (wo as any).stageName || '',
          workCenterId: wo.work_center_id || (wo as any).workCenterId || '',
          workCenterName: wo.work_center_name || (wo as any).workCenterName || '',
          machineId: wo.machine_id || (wo as any).machineId || null,
          machineName: wo.machine_name || (wo as any).machineName || null,
          operatorId: wo.operator_id || (wo as any).operatorId || null,
          operatorName: wo.operator_name || (wo as any).operatorName || null,
          shiftId: wo.shift_id || (wo as any).shiftId || null,
          shiftName: wo.shift_name || (wo as any).shiftName || null,
          plannedQty: Number(wo.planned_qty || (wo as any).plannedQty || 0),
          completedQty: Number(wo.completed_qty || (wo as any).completedQty || 0),
          rejectedQty: Number(wo.rejected_qty || (wo as any).rejectedQty || 0),
          status: (wo.status as any) || 'PENDING',
          plannedStartDate: wo.planned_start_date || (wo as any).plannedStartDate || '',
          plannedEndDate: wo.planned_end_date || (wo as any).plannedEndDate || '',
          actualStartDate: wo.actual_start_date || (wo as any).actualStartDate || null,
          actualEndDate: wo.actual_end_date || (wo as any).actualEndDate || null,
          plannedTimeMinutes: Number(wo.planned_time_minutes || (wo as any).plannedTimeMinutes || 0),
          operatorsRequired: Number((wo as any).operators_required ?? 1) || 1,
          stageType: (wo as any).stage_type || 'IN_HOUSE',
          subcontractorName: (wo as any).subcontractor_name || null,
          actualTimeMinutes: wo.actual_time_minutes !== undefined ? (wo.actual_time_minutes ? Number(wo.actual_time_minutes) : null) : null,
          notes: wo.notes || null,
          warehouseId: wo.warehouse_id || (wo as any).warehouseId || null,
        }));
      }
      setWorkOrders(fetchedWOs);

      // 2. Process Work Centers
      let fetchedWCs: ScheduleWorkCenter[] = [];
      if (wcRes.status === 'fulfilled' && wcRes.value?.success && Array.isArray(wcRes.value.data) && wcRes.value.data.length > 0) {
        fetchedWCs = wcRes.value.data.map((wc: any) => ({
          id: wc.id,
          code: wc.code || '',
          name: wc.name || wc.work_center_name || 'Work Center',
          type: wc.type || 'PROCESS',
          capacityPerHour: Number(wc.capacity_per_hour || wc.capacityPerHour || 10),
          hoursPerDay: Number(wc.working_hours_per_day ?? 8) || 8,
          efficiencyPct: Number(wc.efficiency_pct ?? 100) || 100,
          workingDays: [...new Set<string>((wc.shifts || []).flatMap((s: any) => s.working_days || []))],
        }));
      } else {
        fetchedWCs = [];
      }

      // Dynamically add any missing work centers referenced by work orders
      fetchedWOs.forEach((wo) => {
        if (wo.workCenterName && !fetchedWCs.some((wc) => wc.name.toLowerCase() === wo.workCenterName.toLowerCase() || wc.id === wo.workCenterId)) {
          fetchedWCs.push({
            id: wo.workCenterId || wo.workCenterName,
            name: wo.workCenterName,
            type: 'PROCESS',
            capacityPerHour: 10,
            hoursPerDay: 8,
            efficiencyPct: 100,
            workingDays: [],
          });
        }
      });
      setWorkCenters(fetchedWCs);

      // 3. Process Machines
      let fetchedMachines: ScheduleMachine[] = [];
      if (mcRes.status === 'fulfilled' && mcRes.value?.success && Array.isArray(mcRes.value.data)) {
        fetchedMachines = mcRes.value.data.map((mc: MachineResponse) => ({
          id: mc.id,
          name: mc.name,
          workCenterId: mc.work_center_id || (mc as any).workCenterId || null,
        }));
      }
      setMachines(fetchedMachines);

      // 4. Process Production Orders
      let fetchedPOs: ScheduleProductionOrder[] = [];
      if (poRes.status === 'fulfilled' && poRes.value?.success && Array.isArray(poRes.value.data)) {
        fetchedPOs = poRes.value.data.map((po: ProductionOrderResponse) => ({
          id: po.id,
          poNumber: po.po_number || (po as any).poNumber || '',
          productName: po.item_name || (po as any).itemName || '',
          variantName: (po as any).variantName || '',
        }));
      }
      setProductionOrders(fetchedPOs);
    } catch (err) {
      console.error('Failed to fetch schedule data:', err);
      toast.error(err instanceof Error ? err.message : 'Could not load the schedule');
    } finally {
      setIsLoading(false);
    }
  }, [selectedWarehouseId, toast]);

  useEffect(() => {
    loadScheduleData();
  }, [loadScheduleData]);

  const viewDays = getViewDays(viewMode);
  const dayWidth = 60;
  const totalWidth = viewDays * dayWidth;
  const today = startOfDay(new Date());

  const viewEndDate = addDays(viewStartDate, viewDays - 1);

  /* Build day array */
  const days = useMemo(() => {
    const arr: Date[] = [];
    for (let i = 0; i < viewDays; i++) {
      arr.push(addDays(viewStartDate, i));
    }
    return arr;
  }, [viewStartDate, viewDays]);

  /* Filtered WOs */
  const filteredWOs = useMemo(() => {
    return workOrders.filter((wo) => {
      if (selectedWarehouseId && wo.warehouseId && wo.warehouseId !== selectedWarehouseId) {
        return false;
      }
      if (filters.workCenter && wo.workCenterId !== filters.workCenter) {
        // Older work orders carry only the work center name
        const wcName = workCenters.find((w) => w.id === filters.workCenter)?.name.toLowerCase().trim();
        if (!wcName || wo.workCenterName.toLowerCase().trim() !== wcName) return false;
      }
      if (filters.status && wo.status !== filters.status) return false;
      if (
        filters.productionOrder &&
        wo.productionOrderId !== filters.productionOrder
      )
        return false;
      return true;
    });
  }, [workOrders, workCenters, filters, selectedWarehouseId]);

  /* WOs grouped by work center then machine */
  const getWOsForRow = useCallback(
    (workCenterId: string, machineId: string | null) => {
      const targetWc = workCenters.find((w) => w.id === workCenterId);
      const targetName = targetWc?.name.toLowerCase().trim() || '';

      return filteredWOs.filter((wo) => {
        const idMatch = wo.workCenterId === workCenterId;
        const nameMatch = targetName && wo.workCenterName && (
          wo.workCenterName.toLowerCase().trim() === targetName ||
          wo.workCenterName.toLowerCase().includes(targetName) ||
          targetName.includes(wo.workCenterName.toLowerCase())
        );

        const centerMatches = idMatch || nameMatch;
        if (!centerMatches) return false;

        if (machineId !== null) {
          return wo.machineId === machineId;
        }
        return true;
      });
    },
    [filteredWOs, workCenters],
  );

  /* Days a work order spans, as offsets from the first visible day (end exclusive) */
  const getDaySpan = useCallback(
    (wo: ScheduleWorkOrder) => {
      const startDate = wo.plannedStartDate ? startOfDay(wo.plannedStartDate) : startOfDay(new Date());
      const endDate = wo.plannedEndDate ? startOfDay(wo.plannedEndDate) : startDate;
      const start = daysBetween(viewStartDate, startDate);
      const duration = Math.max(1, daysBetween(startDate, endDate) + 1);
      return { start, end: start + duration, duration };
    },
    [viewStartDate],
  );

  /* Calculate bar position — clipped to the visible range; null when outside it */
  const calcBarStyle = useCallback(
    (wo: ScheduleWorkOrder) => {
      const span = getDaySpan(wo);
      const visibleStart = Math.max(0, span.start);
      const visibleEnd = Math.min(viewDays, span.end);
      if (visibleEnd <= visibleStart) return null;
      return { left: visibleStart * dayWidth, width: (visibleEnd - visibleStart) * dayWidth };
    },
    [getDaySpan, viewDays, dayWidth],
  );

  /* Calculate utilization for a work center over the visible days */
  const calcUtilization = useCallback(
    (wcId: string) => {
      const wos = getWOsForRow(wcId, null);
      if (wos.length === 0) return 0;
      // Each work order contributes the share of its planned time that falls inside the view
      const totalPlannedMinutes = wos.reduce((s, w) => {
        const span = getDaySpan(w);
        const overlapDays = Math.min(viewDays, span.end) - Math.max(0, span.start);
        if (overlapDays <= 0) return s;
        return s + (w.plannedTimeMinutes || 0) * (overlapDays / span.duration);
      }, 0);
      // Available time: the work center's hours × efficiency × machines, on the days its shifts run
      const wc = workCenters.find((w) => w.id === wcId);
      const capacityUnits = Math.max(1, machines.filter((m) => m.workCenterId === wcId).length);
      const minutesPerDay = (wc?.hoursPerDay ?? 8) * 60 * ((wc?.efficiencyPct ?? 100) / 100) * capacityUnits;
      const workingDayCount = days.filter((d) => !wc?.workingDays.length || wc.workingDays.includes(WEEKDAYS[d.getDay()])).length;
      const availableMinutes = minutesPerDay * workingDayCount;
      if (availableMinutes <= 0) return totalPlannedMinutes > 0 ? 999 : 0;
      // Above 100% means more work is planned than the work center can do in the view
      return Math.round((totalPlannedMinutes / availableMinutes) * 100);
    },
    [getWOsForRow, getDaySpan, viewDays, workCenters, machines, days],
  );

  /* Navigation */
  const goPrev = () => setViewStartDate((d) => addDays(d, -viewDays));
  const goNext = () => setViewStartDate((d) => addDays(d, viewDays));
  const goToday = () => setViewStartDate(startOfDay(new Date()));
  const handleDateClick = useCallback((date: Date) => {
    setViewStartDate(startOfDay(date));
  }, []);

  /* PO lookup helper */
  const getPO = (poId: string) =>
    productionOrders.find((p) => p.id === poId);

  /* Unique filter options */
  const poOptions = useMemo(() => {
    const map = new Map<string, string>();
    workOrders.forEach((wo) => {
      map.set(wo.productionOrderId, wo.productionOrderNumber);
    });
    return Array.from(map.entries());
  }, [workOrders]);

  /* Active machines for a work center */
  const getMachines = (wcId: string) =>
    machines.filter((m) => m.workCenterId === wcId);

  /* Clear filters */
  const clearFilters = () =>
    setFilters({ workCenter: '', status: '', productionOrder: '' });

  /* Row height */
  const rowHeight = 48;

  return (
    <AppLayout>
      <div className="flex flex-col h-full">
        {/* ─── Header ─── */}
        <div className="px-6 pt-5 pb-3 bg-white border-b border-gray-200 shrink-0">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-gray-900">
                  Production Schedule
                </h1>
                {selectedWarehouseName && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                    <i className="ri-store-2-line text-xs" />
                    {selectedWarehouseName}
                  </span>
                )}
                {isLoading && (
                  <span className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full animate-pulse">
                    <i className="ri-loader-4-line animate-spin text-xs" /> Loading...
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {formatDateIST(viewStartDate)} — {formatDateIST(viewEndDate)}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Refresh button */}
              <button
                onClick={loadScheduleData}
                disabled={isLoading}
                className="p-2 text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                title="Refresh Schedule Data"
              >
                <i className={`ri-refresh-line text-base ${isLoading ? 'animate-spin' : ''}`} />
              </button>

              {/* View mode toggle */}
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                {(['week', '2weeks', 'month'] as ViewMode[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => setViewMode(m)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      viewMode === m
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {m === 'week' && 'Week'}
                    {m === '2weeks' && '2 Weeks'}
                    {m === 'month' && 'Month'}
                  </button>
                ))}
              </div>

              {/* Date navigation */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={goPrev}
                  className="px-2.5 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors cursor-pointer"
                  title="Previous"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                <button
                  onClick={goToday}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-white rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  Today
                </button>
                <button
                  onClick={goNext}
                  className="px-2.5 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors cursor-pointer"
                  title="Next"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
              <input
                type="date"
                value={toInputDate(viewStartDate)}
                onChange={(e) => {
                  const d = parseInputDate(e.target.value);
                  if (d) setViewStartDate(d);
                }}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* ─── Filters ─── */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 shrink-0">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[190px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Work Center
              </label>
              <SearchableSelect
                value={filters.workCenter}
                onChange={(val) => setFilters((f) => ({ ...f, workCenter: val }))}
                placeholder="All Work Centers"
                searchPlaceholder="Search work center..."
                options={[
                  { value: '', label: 'All Work Centers' },
                  ...workCenters.map((wc) => ({ value: wc.id, label: formatWorkCenter(wc.code, wc.name), sublabel: wc.type })),
                ]}
              />
            </div>

            <div className="min-w-[160px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Status
              </label>
              <SearchableSelect
                value={filters.status}
                onChange={(val) => setFilters((f) => ({ ...f, status: val }))}
                placeholder="All Statuses"
                searchPlaceholder="Search status..."
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'PENDING', label: 'Pending' },
                  { value: 'IN_PROGRESS', label: 'In Progress' },
                  { value: 'COMPLETED', label: 'Completed' },
                  { value: 'ON_HOLD', label: 'On Hold' },
                ]}
              />
            </div>

            <div className="min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Production Order
              </label>
              <SearchableSelect
                value={filters.productionOrder}
                onChange={(val) => setFilters((f) => ({ ...f, productionOrder: val }))}
                placeholder="All Orders"
                searchPlaceholder="Search order..."
                options={[
                  { value: '', label: 'All Orders' },
                  ...poOptions.map(([id, num]) => ({ value: id, label: num })),
                ]}
              />
            </div>

            {(filters.workCenter ||
              filters.status ||
              filters.productionOrder) && (
              <button
                onClick={clearFilters}
                className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear all filters
              </button>
            )}
          </div>
        </div>

        {/* ─── Gantt Chart ─── */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <div className="relative overflow-x-auto flex-1">
            <div style={{ width: 200 + totalWidth }}>
              {/* Date header row */}
              <div className="flex sticky top-0 z-20 bg-white border-b border-gray-200">
                <div className="w-[200px] shrink-0 sticky left-0 z-30 bg-white border-r border-gray-200 px-3 py-2 flex items-end">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Work Center
                  </span>
                </div>
                <div className="flex" style={{ width: totalWidth }}>
                  {days.map((d, i) => {
                    const isToday = isSameDay(d, today);
                    const weekend = isWeekend(d);
                    return (
                      <div
                        key={i}
                        onClick={() => handleDateClick(d)}
                        className={`shrink-0 text-center border-r border-gray-100 cursor-pointer hover:bg-blue-100 transition-colors ${
                          isToday ? 'bg-sky-50' : weekend ? 'bg-gray-50' : ''
                        }`}
                        style={{ width: dayWidth }}
                      >
                        <div className="text-[10px] text-gray-400 pt-1">
                          {dayShort(d)}
                        </div>
                        <div
                          className={`text-xs font-semibold pb-1 ${
                            isToday
                              ? 'text-sky-700'
                              : weekend
                                ? 'text-gray-400'
                                : 'text-gray-700'
                          }`}
                        >
                          {dayNum(d)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Work center rows */}
              {workCenters.map((wc) => {
                const wcMachines = getMachines(wc.id);
                const wcWOs = getWOsForRow(wc.id, null);
                const hasMachines = wcMachines.length > 0;

                return (
                  <div key={wc.id} className="border-b border-gray-100">
                    {/* Work center label + machine sub-rows */}
                    {hasMachines ? (
                      <>
                        {/* Work center row — for WOs without machine */}
                        {wcWOs.length > 0 && (
                          <div className="flex" style={{ height: rowHeight }}>
                            <div className="w-[200px] shrink-0 sticky left-0 z-10 bg-white border-r border-gray-200 px-3 flex items-center">
                              <span className="text-sm font-semibold text-gray-800">
                                {formatWorkCenter(wc.code, wc.name)}
                              </span>
                            </div>
                            <div
                              className="relative flex-1"
                              style={{ width: totalWidth }}
                            >
                              {wcWOs.map((wo) => {
                                const style = calcBarStyle(wo);
                                if (!style) return null;
                                return (
                                  <button
                                    key={wo.id}
                                    onClick={() => setSelectedWO(wo)}
                                    className={`absolute top-1 h-9 rounded text-xs font-medium truncate flex items-center px-2 cursor-pointer hover:brightness-95 transition-all ${getStatusBarClasses(wo.status)}`}
                                    style={{
                                      left: style.left,
                                      width: style.width,
                                    }}
                                  >
                                    {wo.woNumber} — {wo.stageName}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Machine sub-rows */}
                        {wcMachines.map((mc) => {
                          const mcWOs = getWOsForRow(wc.id, mc.id);
                          return (
                            <div
                              key={mc.id}
                              className="flex border-t border-gray-50"
                              style={{ height: rowHeight }}
                            >
                              <div className="w-[200px] shrink-0 sticky left-0 z-10 bg-white border-r border-gray-200 pl-6 pr-3 flex items-center">
                                <span className="text-xs text-gray-600">
                                  {mc.name}
                                </span>
                              </div>
                              <div
                                className="relative flex-1"
                                style={{ width: totalWidth }}
                              >
                                {mcWOs.map((wo) => {
                                  const style = calcBarStyle(wo);
                                  if (!style) return null;
                                  return (
                                    <button
                                      key={wo.id}
                                      onClick={() => setSelectedWO(wo)}
                                      className={`absolute top-1 h-9 rounded text-xs font-medium truncate flex items-center px-2 cursor-pointer hover:brightness-95 transition-all ${getStatusBarClasses(wo.status)}`}
                                      style={{
                                        left: style.left,
                                        width: style.width,
                                      }}
                                    >
                                      {wo.woNumber} — {wo.stageName}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      /* Simple work center row (no machines) */
                      <div className="flex" style={{ height: rowHeight }}>
                        <div className="w-[200px] shrink-0 sticky left-0 z-10 bg-white border-r border-gray-200 px-3 flex items-center">
                          <span className="text-sm font-semibold text-gray-800">
                            {formatWorkCenter(wc.code, wc.name)}
                          </span>
                        </div>
                        <div
                          className="relative flex-1"
                          style={{ width: totalWidth }}
                        >
                          {wcWOs.map((wo) => {
                            const style = calcBarStyle(wo);
                            if (!style) return null;
                            return (
                              <button
                                key={wo.id}
                                onClick={() => setSelectedWO(wo)}
                                className={`absolute top-1 h-9 rounded text-xs font-medium truncate flex items-center px-2 cursor-pointer hover:brightness-95 transition-all ${getStatusBarClasses(wo.status)}`}
                                style={{
                                  left: style.left,
                                  width: style.width,
                                }}
                              >
                                {wo.woNumber} — {wo.stageName}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Capacity utilization bar */}
                    <div className="flex">
                      <div className="w-[200px] shrink-0 sticky left-0 z-10 bg-gray-50 border-r border-gray-200 px-3 py-1.5">
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                          Utilization
                        </span>
                        <div className="text-[10px] text-gray-400" title="Available / day = working hours × efficiency × machines, on its shift days">
                          {wc.hoursPerDay}h × {wc.efficiencyPct}% × {Math.max(1, wcMachines.length)} = {Math.round(wc.hoursPerDay * (wc.efficiencyPct / 100) * Math.max(1, wcMachines.length) * 10) / 10}h/day{wc.workingDays.length > 0 && wc.workingDays.length < 7 ? ` · ${WEEKDAYS.filter((d) => wc.workingDays.includes(d)).join(' ')}` : ''}
                        </div>
                      </div>
                      <div
                        className="flex-1 px-2 py-1.5"
                        style={{ width: totalWidth }}
                      >
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          {(() => {
                            const pct = calcUtilization(wc.id);
                            let barColor = 'bg-emerald-500';
                            if (pct > 100) barColor = 'bg-rose-500';
                            else if (pct > 80) barColor = 'bg-amber-500';
                            return (
                              <div
                                className={`h-full rounded-full ${barColor} transition-all`}
                                style={{ width: `${Math.min(100, pct)}%` }}
                              />
                            );
                          })()}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {(() => {
                            const pct = calcUtilization(wc.id);
                            if (pct === 999) return 'Work planned on non-working days';
                            if (pct > 100) return `${pct}% — Overloaded`;
                            if (pct > 80) return `${pct}% — High load`;
                            return `${pct}%`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ─── Legend ─── */}
          <div className="px-6 py-2.5 bg-white border-t border-gray-200 shrink-0 flex flex-wrap items-center gap-4">
            <span className="text-xs font-medium text-gray-500">Legend:</span>
            {(
              [
                'PENDING',
                'IN_PROGRESS',
                'COMPLETED',
                'ON_HOLD',
              ] as ScheduleWorkOrder['status'][]
            ).map((s) => (
              <div key={s} className="flex items-center gap-1.5">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${getStatusDotClasses(s)}`}
                />
                <span className="text-xs text-gray-600 capitalize">
                  {s.replace('_', ' ').toLowerCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── WO Detail Popup ─── */}
      {selectedWO && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-xs"
          onClick={() => setSelectedWO(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-[420px] max-w-[90vw] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {selectedWO.woNumber}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {selectedWO.stageName}
                </p>
              </div>
              <button
                onClick={() => setSelectedWO(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              {(() => {
                const po = getPO(selectedWO.productionOrderId);
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Production Order
                      </span>
                      <span className="text-sm font-medium text-indigo-600">
                        {selectedWO.productionOrderNumber}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Product</span>
                      <span className="text-sm text-gray-800 text-right font-medium">
                        {po ? (
                          <>
                            {po.productName || '—'}
                            {po.variantName && (
                              <span className="block text-xs text-gray-500 font-normal">
                                {po.variantName}
                              </span>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Planned</span>
                      <span className="text-sm text-gray-800">
                        {formatDateIST(selectedWO.plannedStartDate)} →{' '}
                        {formatDateIST(selectedWO.plannedEndDate)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{selectedWO.stageType === 'JOB_WORK' ? 'Job work by' : 'Machine'}</span>
                      <span className="text-sm text-gray-800">
                        {selectedWO.stageType === 'JOB_WORK'
                          ? selectedWO.subcontractorName || 'Sub-contractor'
                          : selectedWO.machineName || (
                          <span className="text-gray-400 italic">
                            Not assigned
                          </span>
                        )}
                      </span>
                    </div>

                    {selectedWO.stageType !== 'JOB_WORK' && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Crew</span>
                        <span className="text-sm text-gray-800">
                          {selectedWO.operatorsRequired || 1} operator{(selectedWO.operatorsRequired || 1) > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Quantity</span>
                      <span className="text-sm text-gray-800 font-medium">
                        {selectedWO.completedQty}/{selectedWO.plannedQty} units
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Status</span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${getStatusBadgeClasses(selectedWO.status)}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${getStatusDotClasses(selectedWO.status)}`}
                        />
                        {selectedWO.status.replace('_', ' ')}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => {
                  navigate(`/manufacturing/work-orders`);
                  setSelectedWO(null);
                }}
                className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                View Work Order
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
